import { FC } from "react";
import DatePickerLib from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useField } from "formik";
import { Field as ChakraField } from "@chakra-ui/react";

import { CalendarIcon } from "@shared/components/Icons";
import { getUSFormattedDate } from "@shared/utils";

// react-datepicker 9 types its props as a union keyed on `selectsRange`/`selectsMultiple`,
// which does not survive being spread through a wrapper. This field is always a single date,
// so it declares the props it actually forwards instead of inheriting that union.
interface DatePickerProps {
  name: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
}

// v3's `css` prop needs `&` on nested selectors; v2's `sx` accepted bare ones.
const baseStyles = {
  "& .react-datepicker-wrapper": {
    width: "100%",
    position: "relative",
  },
  "& .date_picker_popper": {
    zIndex: 2,
    position: "fixed !important",
  },
  "& .calendar_icon": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "brand.gray",
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    bottom: 0,
    padding: "0 10px",
    zIndex: 1,
  },
  "& .date_picker": {
    width: "100%",
    borderRadius: 8,
  },
};

const DatePicker: FC<DatePickerProps> = ({ name, label, className, ...restProps }) => {
  const [{ value, onChange }] = useField(name);

  return (
    <ChakraField.Root mt={4} className="input" w="100%" css={baseStyles}>
      {label && <ChakraField.Label>{label}</ChakraField.Label>}
      <DatePickerLib
        name={name}
        selected={(value && new Date(value)) || null}
        onChange={(val: Date | null) => {
          onChange({ target: { name, value: val ? getUSFormattedDate(val) : "" } });
        }}
        dateFormat="MM/dd/yyyy"
        className={`date_picker ${className}`}
        popperClassName="date_picker_popper"
        calendarIconClassName="calendar_icon"
        showIcon
        toggleCalendarOnIconClick
        icon={<CalendarIcon color="blue.700" />}
        {...restProps}
      />
    </ChakraField.Root>
  );
};

export default DatePicker;
